import appearanceStyleConstants from '../styles/exports/appearance.export.module.scss';

export type TColorTheme = 'qmulus-light' | 'qmulus-g10' | 'qmulus-g90' | 'qmulus-dark' | 'aichelin-light' | 'aichelin-dark' | 'pt9000-modern' | 'pt9000' | 'galaxy-desert' | 'galaxy-snow' | 'galaxy-void' | 'galaxy-ember';
export type TDensity = 'compact' | 'regular' | 'wide';

// A conditional type only distributes over a union when it checks a type parameter.
type TBrandOfColorTheme<COLOR_THEME extends TColorTheme> = COLOR_THEME extends `${infer BRAND}-${string}` ? BRAND : COLOR_THEME;
export type TColorThemeBrand = TBrandOfColorTheme<TColorTheme>;

/** The key under which exports/*.export.module.scss writes a name: camelCase, e.g. `frameGalaxyDesert`. */
function toExportKey(kebabCaseName: string): string {
  return kebabCaseName.replace(/-(\w)/g, (_hyphenAndLetter, letter: string) => letter.toUpperCase());
}

function splitExportedList(exportedList: string): string[] {
  return exportedList.split(',').map((entry) => entry.trim());
}

/** In the order of `$color-theme-list` in styles/variables/_appearance.scss. */
export const COLOR_THEME_LIST = splitExportedList(appearanceStyleConstants.colorThemeList) as TColorTheme[];
/** In the order of `$density-list` in styles/variables/_appearance.scss. */
export const DENSITY_LIST = splitExportedList(appearanceStyleConstants.densityList) as TDensity[];

export const DEFAULT_COLOR_THEME = appearanceStyleConstants.defaultColorTheme as TColorTheme;
export const DEFAULT_DENSITY = appearanceStyleConstants.defaultDensity as TDensity;

/** The brand of a color theme, the part of its key before the first hyphen, e.g. `aichelin` for `aichelin-dark`. */
export function getColorThemeBrand(colorTheme: TColorTheme): TColorThemeBrand {
  return colorTheme.split('-')[0] as TColorThemeBrand;
}

/** The color theme of the top bar and the navigation rail of a color theme, if it gives them their own (`$frame-color-theme-map`). */
export function getFrameColorTheme(colorTheme: TColorTheme): string | undefined {
  const frameColorThemeMap = appearanceStyleConstants as Record<string, string | undefined>;
  return frameColorThemeMap[toExportKey(`frame-${colorTheme}`)];
}

/** Whether the color theme comes first among the themes of its brand in `COLOR_THEME_LIST`, e.g. to start a row of choices. */
export function isFirstColorThemeOfBrand(colorTheme: TColorTheme): boolean {
  const firstIndexOfBrand = COLOR_THEME_LIST.findIndex((listedColorTheme) => getColorThemeBrand(listedColorTheme) === getColorThemeBrand(colorTheme));
  return firstIndexOfBrand === COLOR_THEME_LIST.indexOf(colorTheme);
}
